// apps/spa/src/components/commands/CommandEditorForm.tsx
// CMD-02 + CMD-03 — Full-page slash command editor.
//
// LOCKED PATTERN (Pattern 7 boundary translator — same as ReminderEditorForm):
//   Resolver binds commandFormSchema (embed as OBJECT; color as hex STRING).
//   Transform-out (hexToInt, JSON.stringify embed) only at onSubmit network boundary.
//
// Layout: same 55%/45% two-column grid as ReminderEditorForm (form left, sticky preview right).
// Responsive: Tabs (Edit/Preview) on mobile (<lg), side-by-side on lg+.
//
// Form fields:
//   Name        — 1-32 chars, regex /^[a-z0-9_-]{1,32}$/, inline error on regex fail
//   Description — 1-100 chars, char counter below field
//   Type        — select (static/dynamic/utility/admin); gates EmbedEditor visibility
//   EmbedEditor — shown only when type==='static'; else shows "Computed at runtime" placeholder
//   Enabled     — Switch (bottom of form)
//   Save button — top-right, disabled while submitting
//
// Phase 7 (Plan 07-05): Type field added. EmbedEditor gated on type==='static'.
// Non-static submit bodies omit embedJson per guardian-worker's type-gate (07-04).
//
// NO channel field, NO schedule field (slash commands are inline, not scheduled).
//
// Error handling:
//   409 name_taken → form.setError('name', { type: 'server', message: '...' })
//   Other ApiError → generic toast
//   401/session-expired → api wrapper fires session-expired event; modal handles it

import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { BASTN_GOLD, ERROR_CODES, type Embed } from '@guardian/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DiscordEmbedPreview,
  EmbedEditor,
  totalEmbedChars,
} from '@/components/EmbedEditor';
import { hexToInt, intToHex, isValidHex } from '@/lib/color';
import {
  useCreateCommand,
  useUpdateCommand,
  type CommandWriteBody,
} from '@/hooks/useCommands';
import { ApiError } from '@/lib/api';
import { TotalCharCounter } from '@/components/reminders/TotalCharCounter';
import { useFormStatePersistence } from '@/hooks/useFormStatePersistence';
import type { SlashCommand } from '@guardian/shared';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Form-shaped schema — Pattern 7 boundary translator
// ---------------------------------------------------------------------------

// Form-side embed schema — embed.color is hex STRING in form state, int at wire boundary.
const URL_OR_EMPTY = z.union([z.string().url(), z.literal('')]);

const embedFieldFormSchema = z.object({
  name: z.string().min(1).max(256),
  value: z.string().min(1).max(1024),
  inline: z.boolean().optional(),
});

const embedFormSchema = z
  .object({
    title: z.string().max(256).optional(),
    description: z.string().max(4096).optional(),
    url: URL_OR_EMPTY.optional(),
    color: z
      .string()
      .optional()
      .refine((v) => v === undefined || v === '' || isValidHex(v), {
        message: 'Color must be a 6-digit hex value (e.g. #d4af37).',
      }),
    footer: z
      .object({
        text: z.string().max(2048),
        icon_url: URL_OR_EMPTY.optional(),
      })
      .optional(),
    thumbnail: z.object({ url: URL_OR_EMPTY }).optional(),
    image: z.object({ url: URL_OR_EMPTY }).optional(),
    fields: z.array(embedFieldFormSchema).max(25).optional(),
  })
  .superRefine((embed, ctx) => {
    let total = 0;
    total += embed.title?.length ?? 0;
    total += embed.description?.length ?? 0;
    total += embed.footer?.text.length ?? 0;
    for (const f of embed.fields ?? []) {
      total += f.name.length + f.value.length;
    }
    if (total > 6000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Total embed text is ${total} characters; max 6000.`,
        path: ['_total'],
      });
    }
  });

// Command type values — matches slashCommands.type enum from 07-00 migration
const COMMAND_TYPES = ['static', 'dynamic', 'utility', 'admin'] as const;
type CommandType = typeof COMMAND_TYPES[number];

const SLASH_NAME_RE = /^[a-z0-9_-]{1,32}$/;

const commandFormSchema = z.object({
  name: z
    .string()
    .regex(SLASH_NAME_RE, 'Lowercase letters, digits, _, - only (1-32 chars)'),
  description: z
    .string()
    .min(1, 'Description is required')
    .max(100, 'Description max 100 chars'),
  type: z.enum(COMMAND_TYPES).default('static'),
  embed: embedFormSchema,
  enabled: z.boolean().default(true),
});

export type CommandFormValues = z.infer<typeof commandFormSchema>;

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const FALLBACK_DEFAULTS: CommandFormValues = {
  name: '',
  description: '',
  type: 'static',
  embed: {
    title: '',
    description: '',
    color: intToHex(BASTN_GOLD),
    footer: { text: '', icon_url: '' },
    thumbnail: { url: '' },
    image: { url: '' },
    url: '',
  },
  enabled: true,
};

function commandToFormValues(cmd: SlashCommand): CommandFormValues {
  let parsedEmbed: Record<string, unknown>;
  try {
    parsedEmbed = JSON.parse(cmd.embedJson) as Record<string, unknown>;
  } catch {
    parsedEmbed = {};
  }

  const colorInt =
    typeof parsedEmbed.color === 'number' ? parsedEmbed.color : BASTN_GOLD;

  return {
    name: cmd.name,
    description: cmd.description,
    type: (cmd.type ?? 'static') as CommandType,
    enabled: cmd.enabled,
    embed: {
      title: typeof parsedEmbed.title === 'string' ? parsedEmbed.title : '',
      description: typeof parsedEmbed.description === 'string' ? parsedEmbed.description : '',
      url: typeof parsedEmbed.url === 'string' ? parsedEmbed.url : '',
      color: intToHex(colorInt),
      footer: {
        text: typeof (parsedEmbed.footer as { text?: string } | undefined)?.text === 'string'
          ? ((parsedEmbed.footer as { text: string }).text) : '',
        icon_url: typeof (parsedEmbed.footer as { icon_url?: string } | undefined)?.icon_url === 'string'
          ? ((parsedEmbed.footer as { icon_url: string }).icon_url) : '',
      },
      thumbnail: {
        url: typeof (parsedEmbed.thumbnail as { url?: string } | undefined)?.url === 'string'
          ? ((parsedEmbed.thumbnail as { url: string }).url) : '',
      },
      image: {
        url: typeof (parsedEmbed.image as { url?: string } | undefined)?.url === 'string'
          ? ((parsedEmbed.image as { url: string }).url) : '',
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Type option labels — LOCKED icons per 07-CONTEXT.md
// ---------------------------------------------------------------------------

const TYPE_OPTIONS: { value: CommandType; label: string }[] = [
  { value: 'static',  label: '📄 Static' },
  { value: 'dynamic', label: '📊 Dynamic' },
  { value: 'utility', label: '👥 Member' },
  { value: 'admin',   label: '🔒 Admin' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CommandEditorFormProps {
  mode: 'new' | 'edit';
  commandId?: string;
  command?: SlashCommand;
}

export function CommandEditorForm({
  mode,
  commandId,
  command,
}: CommandEditorFormProps) {
  const navigate = useNavigate();
  const createMutation = useCreateCommand();
  const updateMutation = useUpdateCommand(commandId ?? '');

  const defaultValues = useMemo(
    () => (command ? commandToFormValues(command) : FALLBACK_DEFAULTS),
    [command],
  );

  const form = useForm<CommandFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(commandFormSchema) as any,
    defaultValues,
  });

  // Reset form when command data becomes available async (edit mode).
  useEffect(() => {
    if (command) form.reset(commandToFormValues(command));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command]);

  const embedValues = useWatch({ control: form.control, name: 'embed' });
  const total = totalEmbedChars(embedValues as Partial<Embed>);

  const descValue = useWatch({ control: form.control, name: 'description' });

  // Watch the type field to gate EmbedEditor visibility
  const watchedType = useWatch({ control: form.control, name: 'type' });
  const isStatic = (watchedType ?? 'static') === 'static';

  // 401-modal form state persistence — same pattern as ReminderEditorForm.
  useFormStatePersistence({
    formId: mode === 'new' ? 'command-new' : `command-${commandId}`,
    route: mode === 'new' ? '/commands/new' : `/commands/${commandId}/edit`,
    getFormValues: () => form.getValues(),
    setFormValues: (v) => form.reset(v as CommandFormValues),
  });

  async function onSubmit(values: CommandFormValues) {
    try {
      // --------- Boundary translator (form -> wire) ---------
      let wireBody: CommandWriteBody;

      if (isStatic) {
        // Static: include embed JSON in wire body
        const colorHex = values.embed.color;
        const color =
          colorHex && isValidHex(colorHex) ? hexToInt(colorHex) : undefined;

        const embedObj: Record<string, unknown> = { ...values.embed, color };
        if (color === undefined) delete embedObj.color;
        if (!embedObj.title) delete embedObj.title;
        if (!embedObj.description) delete embedObj.description;
        if (!embedObj.url) delete embedObj.url;
        const footer = embedObj.footer as { text?: string; icon_url?: string } | undefined;
        if (!footer?.text) {
          delete embedObj.footer;
        } else if (!footer.icon_url) {
          delete (footer as { icon_url?: string }).icon_url;
        }
        const thumbnail = embedObj.thumbnail as { url?: string } | undefined;
        if (!thumbnail?.url) delete embedObj.thumbnail;
        const image = embedObj.image as { url?: string } | undefined;
        if (!image?.url) delete embedObj.image;

        wireBody = {
          name: values.name,
          description: values.description,
          type: 'static',
          embedJson: JSON.stringify(embedObj),
          enabled: values.enabled,
        };
      } else {
        // Non-static: omit embedJson — guardian-worker skips embed validation for non-static
        wireBody = {
          name: values.name,
          description: values.description,
          type: values.type,
          enabled: values.enabled,
        };
      }
      // ---------------------------------------------------------

      if (mode === 'new') {
        await createMutation.mutateAsync(wireBody);
        toast.success('✓ Saved — registering with Discord…');
      } else {
        await updateMutation.mutateAsync(wireBody);
        toast.success('✓ Updated — re-registering with Discord…');
      }

      navigate('/commands');
    } catch (err) {
      if (err instanceof ApiError) {
        // 409 name_taken → inline form error on Name field
        if (err.code === ERROR_CODES.NAME_TAKEN) {
          form.setError('name', {
            type: 'server',
            message: 'A slash command with this name already exists.',
          });
          return;
        }
        // Map server validation issues to RHF
        const details = err.details as
          | { issues?: Array<{ path: Array<string | number>; message: string }> }
          | undefined;
        const issues = details?.issues;
        if (Array.isArray(issues)) {
          for (const issue of issues) {
            const path = Array.isArray(issue.path)
              ? issue.path.join('.')
              : String(issue.path);
            form.setError(path as Parameters<typeof form.setError>[0], {
              type: 'server',
              message: issue.message,
            });
          }
          return;
        }
      }
      toast.error('Could not save command. Try again.');
    }
  }

  const isSubmitting = form.formState.isSubmitting;

  return (
    <FormProvider {...form}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSubmit={(form as any).handleSubmit(onSubmit)}
        className="space-y-6"
        noValidate
      >
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary/80">Commands</p>
            <h1 className="font-display text-3xl">
              {mode === 'new' ? 'New command' : 'Edit command'}
            </h1>
          </div>
          <Button type="submit" disabled={isSubmitting}>
            Save
          </Button>
        </header>

        {isStatic && <TotalCharCounter total={total} max={6000} />}

        <div className="lg:grid lg:grid-cols-[55%_45%] lg:gap-8">
          <div className="glass p-6 space-y-6">
            {isStatic && (
              <Tabs defaultValue="edit" className="lg:hidden">
                <TabsList>
                  <TabsTrigger value="edit">Edit</TabsTrigger>
                  <TabsTrigger value="preview">Preview · Live</TabsTrigger>
                </TabsList>
                <TabsContent value="edit" />
                <TabsContent value="preview">
                  <DiscordEmbedPreview embed={embedValues as Embed} />
                </TabsContent>
              </Tabs>
            )}
            <CommandFormSection
              form={form}
              descLength={descValue?.length ?? 0}
              isStatic={isStatic}
            />
          </div>
          {isStatic && (
            <div className="hidden lg:block">
              <div className="sticky top-20">
                <DiscordEmbedPreview embed={embedValues as Embed} />
              </div>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
}

// ---------------------------------------------------------------------------
// Form section — extracted to prevent duplicate RHF field mount issues
// ---------------------------------------------------------------------------

function CommandFormSection({
  form,
  descLength,
  isStatic,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  descLength: number;
  isStatic: boolean;
}) {
  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">/</span>
                <Input
                  maxLength={32}
                  className="pl-6"
                  placeholder="command-name"
                  {...field}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Input maxLength={100} placeholder="What does this command do?" {...field} />
            </FormControl>
            <div className="mt-1 text-right">
              <span className="text-xs text-muted-foreground">{descLength}/100</span>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Type selector — plain <select> to avoid adding a new shadcn primitive */}
      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="command-type-select">Type</FormLabel>
            <FormControl>
              <select
                id="command-type-select"
                aria-label="Type"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                className={cn(
                  'h-9 w-full rounded-md border border-input bg-transparent px-3 py-1',
                  'text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* EmbedEditor — only rendered for static type */}
      {isStatic ? (
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80 mb-3">Embed</p>
          <EmbedEditor name="embed" />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          <p className="font-medium">Computed at runtime by handler</p>
          <p className="mt-1">
            This command type is implemented in code; embed content is generated when invoked.
          </p>
        </div>
      )}

      <FormField
        control={form.control}
        name="enabled"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between rounded-md border p-4">
            <div>
              <FormLabel className="text-sm font-medium">Enabled</FormLabel>
              <p className="text-xs text-muted-foreground mt-0.5">
                Disabled commands are deregistered from Discord on save.
              </p>
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-label="Enable command"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
